import React, { memo, useCallback } from 'react';
import { useIntersection } from '@mantine/hooks';
import {
  GetSavedSearchesParams,
  GetSavedSearchesResult,
  SavedSearchItem,
} from 'providers/contexts/extensions/SavedSearches';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Popover,
  CircularProgress,
  ButtonProps,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SkeletonBox from 'components/generic/SkeletonBox';
import { bloxValues } from 'themes/constants';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import { uniqueBy } from 'remeda';
import { getSolidThemeGray } from 'utility/styles';
import { mergeDeep } from 'remeda';
import { useCosmos } from 'providers/contexts/CosmosContext';

// --- Utility / Constants

/**
 * Quick Filter QP - quickFilterId
 * Usage see EditorialQuickFilterSelector.tsx
 * and see SearchContextProvider.tsx (getSearchItemInfoBase)
 *
 * Exporting from here as it will be generic many views.
 *
 * When navigating from the saved search page, this qp will
 * be appended to the route params... eg:
 * /app/search?&(...saved-search-params)&quickFilterId=abc123def...
 *
 * If this ID is present upon load the selector can detect and fetch
 * the saved search by that ID, then applies the filter params
 * and remove the quickFilterId qp after application.
 */

export const QUICKFILTER_ID_QP = 'quickFilterId';

export function getQuickFilterIdQp(searchParams: URLSearchParams): string | null {
  const id = searchParams.get(QUICKFILTER_ID_QP);
  if (!id || id.length === 0) {
    return null;
  }
  return id;
}

type NormQpStrParams = {
  qpstr: string;
  ignoreKeys?: string[];
};

/**
 * helper checks eg "foo=1&bar=2" vs "bar=2&foo=1"
 * treated as equal, with optional ignore eg sort/dir
 */
export function normalizeQpStr({ qpstr, ignoreKeys = [] }: NormQpStrParams): string {
  if (!qpstr) {
    return qpstr;
  }
  const params = new URLSearchParams(qpstr);
  // normalize for ignored keys
  const ignored = ignoreKeys.map((key) => key.toLowerCase());
  const filtered = Array.from(params.entries()).filter(
    ([k]) => !ignored.includes(k.toLowerCase()),
  );

  // sort by key and join for string comparison
  const result = filtered
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  return result;
}

/**
 * SavedSearchSelector
 * Quick Filters Selector
 */

type ViewTab = 'shared' | 'personal';

type SavedSearchSelectorProps = {
  addPayload?: GetSavedSearchesParams;
  value?: SavedSearchItem | null;
  clientItems?: SavedSearchItem[]; // Inject client side items to the list, will be deduped by id.
  onSelectCb?: (event: React.MouseEvent<HTMLDivElement>, item: SavedSearchItem) => void;
  createNewCb?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  // external load/disable states, eg when fetching data
  disabled?: boolean;
  loading?: boolean;
  hidden?: boolean;
};

export const SavedSearchSelector = memo(
  ({
    addPayload,
    value,
    clientItems,
    onSelectCb,
    createNewCb,
    disabled = false,
    hidden = false,
    loading = false,
  }: SavedSearchSelectorProps) => {
    // Hooks / Utilities
    const theme = useTheme();
    const { userInfo } = useCosmos();
    const userUuid = userInfo?.uuid || '';

    const { t } = useTranslation();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    const apiPayload: GetSavedSearchesParams = useMemo(() => {
      return addPayload ?? {};
    }, [addPayload]);

    // Intersection observer for infinite scroll
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    // UI States
    const popoverAnchorRef = useRef<HTMLButtonElement | null>(null);
    const [tab, setTab] = useState<ViewTab>('personal');
    const [popoverOpen, setPopoverOpen] = useState(false);
    const isHidden = Boolean(hidden);
    const isDisabled = Boolean(disabled);
    const isLoading = Boolean(loading); // btn spinner
    const hasValue = Boolean(value);

    // Translation Strings
    const strError = t('blox.general.error');
    const strLoading = t('blox.general.loading');
    const strQuickFilters = t('blox.search.saved.quick_filters');
    const strSharedFilters = t('blox.search.saved.shared_with_org');
    const strPersonalFilters = t('blox.search.saved.my_filters');
    const strCreateNewFilter = t('blox.search.saved.create_new_filter');
    const strNoResults = t('blox.general.no_results');

    // Infinite query for saved searches (quick filters)
    const savedSearchesQuery = useInfiniteQuery<GetSavedSearchesResult, ApiErrorResponse>(
      {
        queryKey: ['getSavedSearches', apiPayload],
        queryFn: async ({ pageParam }) => {
          // Default query params page/limit
          const defaultQuery: GetSavedSearchesParams = {
            query: {
              page: pageParam as string,
              limit: 99,
            },
          };
          // Merge in with props payload params
          const payload = mergeDeep(defaultQuery, apiPayload);
          const result = await dataProvider.getSavedSearches(payload);
          return result;
        },
        getNextPageParam: (lastPage) => lastPage?.data?.next,
        enabled: true,
        gcTime: 0,
        staleTime: 0,
        retry: false,
        initialPageParam: '',
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    );

    const {
      data: queryData,
      isPending: queryIsPending,
      isError: queryIsError,
      error: queryError,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    } = savedSearchesQuery;

    // Flat list of addResults if provided + fetched results.
    // Dedupe by id, with prop results on top as priority item.
    const allItems = useMemo<SavedSearchItem[]>(() => {
      const injected = clientItems ?? [];
      const noItems = !queryData && !injected?.length;
      // no client side items and no query data
      if (noItems) {
        return [];
      }
      // flatten fetched page results into a single array
      const flatData = queryData?.pages?.flatMap((page) => page?.data?.items ?? []);
      const fetched = flatData ?? [];
      const combined = [...injected, ...fetched];

      // dedupe by id as we may inject client side items
      const deduped = uniqueBy(combined, (x) => x?.id ?? '');
      return deduped;
    }, [clientItems, queryData]);

    // Split by personal or shared with org items
    const sharedOrgItems = useMemo(
      () => allItems.filter((item) => item?.user_id !== userUuid),
      [allItems, userUuid],
    );

    const personalItems = useMemo(
      () => allItems.filter((item) => item?.user_id === userUuid),
      [allItems, userUuid],
    );

    // Which tabs data to be showing...
    const listItems = useMemo(() => {
      return tab === 'shared' ? sharedOrgItems : personalItems;
    }, [tab, sharedOrgItems, personalItems]);

    // Use name from saved search item or quick filters label
    const buttonLabel = useMemo(() => {
      if (value) {
        return value?.name ?? '';
      }
      return strQuickFilters;
    }, [value, strQuickFilters]);

    // Event Handlers
    const handleOpen = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          return;
        }
        setPopoverOpen(true);
      },
      [isDisabled],
    );

    const handleClose = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setPopoverOpen(false);
    }, []);

    // Handle item selection
    const handleSelect = useCallback(
      (event: React.MouseEvent<HTMLDivElement>, item: SavedSearchItem) => {
        onSelectCb?.(event, item);
        setPopoverOpen(false);
      },
      [onSelectCb],
    );

    // Handle create new filter
    const handleNewFilter = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (createNewCb) {
          createNewCb(event);
        }
      },
      [createNewCb],
    );

    // Tabs change
    const handleTabChange = useCallback((ev: React.SyntheticEvent, newValue: ViewTab) => {
      setTab(newValue);
    }, []);

    // Theme colors matching select inputs
    const bgSolidGray = getSolidThemeGray(theme);

    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

    // per meeting if a quickfilter is applied
    // use primary color, else gray color (info)
    const btnColor: ButtonProps['color'] = hasValue ? 'primary' : 'info';

    return (
      <Fragment>
        {isMobileOrTablet ? (
          <Tooltip
            title={strQuickFilters}
            disableInteractive
          >
            <IconButton
              id="saved-search-selector-button"
              data-testid="saved-search-selector-button"
              color={hasValue ? 'primary' : 'default'}
              aria-label={strQuickFilters}
              ref={popoverAnchorRef}
              onClick={handleOpen}
              disabled={isDisabled}
              aria-haspopup="listbox"
              aria-expanded={popoverOpen ? 'true' : undefined}
              aria-controls={popoverOpen ? 'saved-search-selector-popper' : undefined}
              sx={{ p: 0.5 }}
            >
              {isLoading ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <BookmarkIcon color={hasValue ? 'primary' : 'disabled'} />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            id="saved-search-selector-button"
            data-testid="saved-search-selector-button"
            variant="outlined"
            color={btnColor}
            title={strQuickFilters}
            aria-label={strQuickFilters}
            ref={popoverAnchorRef}
            onClick={handleOpen}
            startIcon={
              isLoading ? (
                <CircularProgress
                  size={14}
                  color="inherit"
                />
              ) : (
                <BookmarkIcon color={hasValue ? 'primary' : 'disabled'} />
              )
            }
            endIcon={<ArrowDropDownIcon />}
            disabled={isDisabled}
            aria-haspopup="listbox"
            aria-expanded={popoverOpen ? 'true' : undefined}
            aria-controls={popoverOpen ? 'saved-search-selector-popper' : undefined}
            sx={{
              py: 1.25,
              px: 0.5,
              borderRadius: 1.75, // Aligns with search input border radius for visual consistency
              maxWidth: 150,
              maxHeight: 36,
              textAlign: 'left',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              '& .MuiButton-startIcon': {
                p: 0,
                m: 0,
                mr: 0.5,
              },
              '& .MuiButton-endIcon': {
                p: 0,
                m: 0,
                ml: 0.5,
              },
            }}
          >
            <Box
              id="saved-search-selector-button-label"
              data-testid="saved-search-selector-button-label"
              title={buttonLabel}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                whiteSpace: 'normal',
                minWidth: 0,
                flex: 1,
                fontWeight: bloxValues.fontWeightMedium,
                fontSize: bloxValues.smallFontSizeRem,
              }}
            >
              {buttonLabel}
            </Box>
          </Button>
        )}

        <Popover
          anchorEl={popoverAnchorRef.current}
          open={popoverOpen}
          hidden={isHidden}
          onClose={handleClose}
          // tabs underline doesnt work well with
          // the transition on popover so disabling
          keepMounted={false}
          disablePortal={false}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          elevation={3}
          id="saved-search-selector-popover"
          data-testid="saved-search-selector-popover"
          slotProps={{
            paper: {
              sx: {
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                gridTemplateAreas: `"tabs" "list" "end"`,
                container: 'selectgrid / normal',
                overflow: 'hidden',
                minWidth: 150,
                width: 300,
                maxHeight: 400,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[3],
              },
            },

            transition: {
              timeout: {
                enter: 0,
                exit: 0,
              },
              in: popoverOpen,
              unmountOnExit: true,
              appear: true,
            },
          }}
        >
          {/* Tabs Area */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            aria-label={strQuickFilters}
            sx={{
              gridArea: 'tabs',
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: bgSolidGray,
              // smaller tabs / icons
              minHeight: 42,
              height: 42,
              '& .MuiTab-root': {
                minHeight: 42,
                height: 42,
                py: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: bloxValues.iconSizeRegular,
                },
              },
            }}
          >
            <Tab
              data-testid="saved-search-selector-tab-personal"
              value="personal"
              aria-label={strPersonalFilters}
              title={strPersonalFilters}
              icon={
                <Tooltip
                  title={strPersonalFilters}
                  disableInteractive
                >
                  <PersonIcon />
                </Tooltip>
              }
            />
            <Tab
              data-testid="saved-search-selector-tab-shared"
              value="shared"
              aria-label={strSharedFilters}
              title={strSharedFilters}
              icon={
                <Tooltip
                  title={strSharedFilters}
                  disableInteractive
                >
                  <PublicIcon />
                </Tooltip>
              }
            />
          </Tabs>

          {/* Saved Search Items List area */}
          <Box
            sx={{
              gridArea: 'list',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {queryIsPending ? (
              <SkeletonBox
                text={strLoading}
                width="100%"
                height="40px"
              />
            ) : queryIsError ? (
              <ErrorAlerts
                headerText={strError}
                severity="warning"
                buttonText="Retry"
                buttonOnClick={() => refetch()}
                showDetails={true}
                sx={{
                  margin: 1,
                  height: 'fit-content',
                }}
                errors={[
                  {
                    status: `${queryError?.status}`,
                    message: `${queryError?.message}`,
                    fullDetails: queryError,
                  },
                ]}
              />
            ) : listItems.length === 0 ? (
              <Typography
                id="saved-search-selector-no-results"
                data-testid="saved-search-selector-no-results"
                sx={{
                  py: 2,
                  textAlign: 'center',
                  color: theme.palette.text.disabled,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                {strNoResults}
              </Typography>
            ) : (
              <List
                id="saved-search-selector-list"
                data-testid="saved-search-selector-list"
                dense={true}
                disablePadding={true}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto',
                }}
              >
                {listItems.map((item, idx) => {
                  const itemId = item?.id ?? '';
                  const reactKey = `${itemId}`;
                  const itemName = item?.name || '';
                  const isSelected = value?.id === item?.id;
                  const isLastItem = Boolean(idx === listItems?.length - 1);
                  const fontWt = isSelected
                    ? bloxValues.fontWeightMedium
                    : bloxValues.fontWeightRegular;

                  // Dont render anything without an ID
                  if (!itemId) {
                    return null;
                  }

                  return (
                    <Fragment key={reactKey}>
                      <ListItem
                        aria-selected={isSelected}
                        disableGutters={true}
                        disablePadding={true}
                        title={itemName}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          fontWeight: fontWt,
                        }}
                      >
                        <ListItemButton
                          data-testid={`saved-search-item-${itemId}`}
                          disabled={isDisabled}
                          disableGutters={true}
                          tabIndex={0}
                          selected={isSelected}
                          aria-label={itemName}
                          onClick={(ev) => handleSelect(ev, item)}
                          sx={{
                            px: 1,
                            py: 0.5,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: bloxValues.regularFontSizeRem,
                                  fontWeight: fontWt,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 1,
                                  whiteSpace: 'normal',
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                {itemName}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>

                      {/* Intersection Loader for infinite scroll */}
                      {isLastItem && (
                        <IntersectionLoader
                          ixRef={ixRef}
                          entry={entry}
                          hasNextPage={hasNextPage}
                          isFetching={isFetchingNextPage}
                          fetchNextPage={fetchNextPage}
                          compact={true}
                          hideOnPagesEnd={true}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Create New Filter button at bottom */}
          <Box
            sx={{
              gridArea: 'end',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: bgSolidGray,
            }}
          >
            <Button
              data-testid="saved-search-create-new-filter-button"
              variant="text"
              color="primary"
              onClick={handleNewFilter}
              fullWidth={true}
              tabIndex={0}
              sx={{
                p: 1,
                bgcolor: 'inherit',
              }}
            >
              {strCreateNewFilter}
            </Button>
          </Box>
        </Popover>
      </Fragment>
    );
  },
);
